/**
 * @file src/helpers/userPermissions.ts
 * @exports buildPermissionUpdate, createPermissionRows, getCraAccessSummaries, getEffectiveRoleAssignment, hasGlobalPermissionRow, isUserRoleVisible, PermissionRow, permissionRowsForSave, RoleAssignmentSummary, viewerHasCraAffiliates
 * @summary Normalizes organization-scoped roles and CRA-typed access summaries for display and updates.
 */

import { RoleEnum, type UserPermissionsInput } from "src/generated/graphql";

const GLOBAL_ROLE = "SUPER_ADMIN";
const RESTRICTED_ROLES = new Set([GLOBAL_ROLE, "CEREBRUM_TEAM", "TAZ_INTEGRATION"]);
const CRA_AFFILIATE_ACCESS_ROLES = new Set(["CRA_ADMIN", "PROCESSOR", "LIMITED_PROCESSOR"]);

const ROLE_PRECEDENCE = [
	"SUPER_ADMIN",
	"CEREBRUM_TEAM",
	"TAZ_INTEGRATION",
	"CRA_ADMIN",
	"ORGANIZATION_ADMIN",
	"INTEGRATION_ADMIN",
	"PROCESSOR",
	"LIMITED_PROCESSOR",
	"USER",
] as const;

const ROLE_PRECEDENCE_RANK = new Map(ROLE_PRECEDENCE.map((role, index) => [role, ROLE_PRECEDENCE.length - index]));

export interface RoleAssignmentSummary {
	id: string;
	organization?: { id?: string | null; name?: string | null } | null;
	organizationId?: string | null;
	roleV2: { name: string };
}

export interface PermissionRow {
	assignmentId?: string;
	clientId: string;
	organizationId: string;
	organizationName?: string;
	roleName: string;
	scope: "global" | "organization" | "unscoped";
}

interface CraOrganizationSummary {
	affiliateOrgTo?: Array<{
		primaryOrg: { id: string; name: string };
		type: string;
	}> | null;
	id: string;
	name: string;
}

interface CraAccessSummary {
	affiliateOrganizations: Array<{ id: string; name: string }>;
	primaryOrganizationId: string;
}

const PERMISSION_SCOPE_ORDER: Record<PermissionRow["scope"], number> = {
	global: 0,
	organization: 1,
	unscoped: 2,
};

interface CreatePermissionRowsProps {
	roleAssignments?: RoleAssignmentSummary[] | null;
}

interface GetEffectiveRoleAssignmentProps {
	organizationId: string;
	roleAssignments?: RoleAssignmentSummary[] | null;
}

interface BuildPermissionUpdateProps {
	originalRoleAssignments: RoleAssignmentSummary[];
	permissionRows: PermissionRow[];
}

interface IsUserRoleVisibleProps {
	roleName: string;
	viewerBelongsToCra?: boolean;
	viewerRole?: string | null;
	viewerRoles?: Iterable<string> | null;
}

interface PermissionRowsForSaveProps {
	editedRows: PermissionRow[];
	originalRoleAssignments: RoleAssignmentSummary[];
	viewerBelongsToCra?: boolean;
	viewerRole?: string | null;
	viewerRoles?: Iterable<string> | null;
}

interface GetCraAccessSummariesProps {
	organizations?: CraOrganizationSummary[] | null;
	permissionRows: PermissionRow[];
}

/** True when one of the viewer's organizations is a CRA with at least one affiliate. */
export const viewerHasCraAffiliates = (
	organizations?: readonly { primaryOrgFor?: readonly unknown[] | null }[] | null,
) => organizations?.some((organization) => (organization.primaryOrgFor?.length ?? 0) > 0) ?? false;

/**
 * Hides TazWorks, Cerebrum Team, and Super Admin unless the viewer holds that role or is a Super Admin.
 * CRA Admin, Processor, and Limited Processor stay hidden from client organizations.
 */
export const isUserRoleVisible = ({
	roleName,
	viewerBelongsToCra = false,
	viewerRole,
	viewerRoles,
}: IsUserRoleVisibleProps) => {
	const held = new Set(viewerRoles ?? []);
	if (viewerRole) held.add(viewerRole);
	if (held.has(GLOBAL_ROLE)) return true;
	if (RESTRICTED_ROLES.has(roleName)) return held.has(roleName);
	if (CRA_AFFILIATE_ACCESS_ROLES.has(roleName)) return viewerBelongsToCra;
	return true;
};

/** Matches the organization-scoped roles Cortex expands across CRA affiliates. */
const isCraAffiliateAccessRole = (roleName: string) => CRA_AFFILIATE_ACCESS_ROLES.has(roleName);

/** Returns whether Cortex treats a role assignment as globally effective. */
const isGlobalRoleAssignment = (roleAssignment: RoleAssignmentSummary) => roleAssignment.roleV2.name === GLOBAL_ROLE;

/** Returns whether any row is globally effective, which supersedes every organization-scoped role. */
export const hasGlobalPermissionRow = (permissionRows: PermissionRow[]) =>
	permissionRows.some((permission) => permission.scope === "global");

/** Converts raw role assignments into rows without losing their organization-to-role relationship. */
export const createPermissionRows = ({ roleAssignments }: CreatePermissionRowsProps): PermissionRow[] =>
	(roleAssignments ?? [])
		.map((roleAssignment) => {
			const global = isGlobalRoleAssignment(roleAssignment);
			const organizationId = roleAssignment.organizationId ?? roleAssignment.organization?.id ?? "";
			const scope: PermissionRow["scope"] = global ? "global" : organizationId ? "organization" : "unscoped";

			return {
				assignmentId: roleAssignment.id,
				clientId: roleAssignment.id,
				organizationId,
				organizationName: roleAssignment.organization?.name ?? undefined,
				roleName: roleAssignment.roleV2.name,
				scope,
			};
		})
		.sort((a, b) => {
			const scopeComparison = PERMISSION_SCOPE_ORDER[a.scope] - PERMISSION_SCOPE_ORDER[b.scope];
			if (scopeComparison !== 0) return scopeComparison;

			return (a.organizationName ?? a.organizationId).localeCompare(b.organizationName ?? b.organizationId);
		});

/** Keeps organization roles the viewer cannot see from being deleted by an edit. */
export const permissionRowsForSave = ({
	editedRows,
	originalRoleAssignments,
	viewerBelongsToCra,
	viewerRole,
	viewerRoles,
}: PermissionRowsForSaveProps): PermissionRow[] => {
	const hidden = createPermissionRows({ roleAssignments: originalRoleAssignments }).filter(
		(row) =>
			row.scope === "organization" &&
			!isUserRoleVisible({ roleName: row.roleName, viewerBelongsToCra, viewerRole, viewerRoles }),
	);
	const hiddenIds = new Set(hidden.flatMap((row) => (row.assignmentId ? [row.assignmentId] : [])));
	return [...editedRows.filter((row) => !row.assignmentId || !hiddenIds.has(row.assignmentId)), ...hidden];
};

/** Mirrors Cortex role resolution, including global SUPER_ADMIN and same-org role precedence. */
export const getEffectiveRoleAssignment = ({
	organizationId,
	roleAssignments,
}: GetEffectiveRoleAssignmentProps): RoleAssignmentSummary | undefined => {
	const globalAssignment = roleAssignments?.find(isGlobalRoleAssignment);
	if (globalAssignment) return globalAssignment;

	return roleAssignments
		?.filter((roleAssignment) => {
			const assignmentOrganizationId = roleAssignment.organizationId ?? roleAssignment.organization?.id;
			return assignmentOrganizationId === organizationId;
		})
		.sort(
			(a, b) =>
				(ROLE_PRECEDENCE_RANK.get(b.roleV2.name as (typeof ROLE_PRECEDENCE)[number]) ?? 0) -
				(ROLE_PRECEDENCE_RANK.get(a.roleV2.name as (typeof ROLE_PRECEDENCE)[number]) ?? 0),
		)[0];
};

/**
 * Finds assigned affiliate organizations reached through each effective CRA-capable role.
 * A global assignment is effective everywhere, so no bounded CRA summaries apply when one is present.
 */
export const getCraAccessSummaries = ({
	organizations,
	permissionRows,
}: GetCraAccessSummariesProps): CraAccessSummary[] => {
	if (hasGlobalPermissionRow(permissionRows)) return [];

	const assignedOrganizationIds = new Set(
		permissionRows
			.filter((permission) => permission.scope === "organization")
			.map((permission) => permission.organizationId)
			.filter(Boolean),
	);
	const effectiveRoleByOrganizationId = new Map<string, string>();

	for (const permission of permissionRows) {
		if (permission.scope !== "organization" || !permission.organizationId) continue;

		const currentRole = effectiveRoleByOrganizationId.get(permission.organizationId);
		if (
			!currentRole ||
			(ROLE_PRECEDENCE_RANK.get(permission.roleName as (typeof ROLE_PRECEDENCE)[number]) ?? 0) >
				(ROLE_PRECEDENCE_RANK.get(currentRole as (typeof ROLE_PRECEDENCE)[number]) ?? 0)
		) {
			effectiveRoleByOrganizationId.set(permission.organizationId, permission.roleName);
		}
	}

	const affiliatesByPrimaryOrganizationId = new Map<string, Map<string, string>>();
	for (const organization of organizations ?? []) {
		if (!assignedOrganizationIds.has(organization.id)) continue;

		for (const relation of organization.affiliateOrgTo ?? []) {
			if (relation.type !== "CRA") continue;
			const primaryOrganizationId = relation.primaryOrg.id;
			const primaryRole = effectiveRoleByOrganizationId.get(primaryOrganizationId);
			if (!assignedOrganizationIds.has(primaryOrganizationId) || !primaryRole) continue;
			if (!isCraAffiliateAccessRole(primaryRole)) continue;

			const affiliates = affiliatesByPrimaryOrganizationId.get(primaryOrganizationId) ?? new Map<string, string>();
			affiliates.set(organization.id, organization.name);
			affiliatesByPrimaryOrganizationId.set(primaryOrganizationId, affiliates);
		}
	}

	return Array.from(affiliatesByPrimaryOrganizationId, ([primaryOrganizationId, affiliates]) => ({
		affiliateOrganizations: Array.from(affiliates, ([id, name]) => ({ id, name })).sort((a, b) =>
			a.name.localeCompare(b.name),
		),
		primaryOrganizationId,
	})).sort((a, b) => a.primaryOrganizationId.localeCompare(b.primaryOrganizationId));
};

/** Sends only explicit changes; Cortex atomically maintains the matching memberships. */
export const buildPermissionUpdate = ({
	originalRoleAssignments,
	permissionRows,
}: BuildPermissionUpdateProps): Pick<UserPermissionsInput, "assignments" | "removeAssignmentIds"> => {
	const originals = new Map(originalRoleAssignments.map((assignment) => [assignment.id, assignment]));
	const retainedIds = new Set(permissionRows.map((permission) => permission.assignmentId));
	const assignments = permissionRows.flatMap((permission) => {
		if (permission.scope !== "organization" || !permission.organizationId || !permission.roleName) return [];
		const original = permission.assignmentId ? originals.get(permission.assignmentId) : undefined;
		if (
			original &&
			(original.organizationId ?? original.organization?.id) === permission.organizationId &&
			original.roleV2.name === permission.roleName
		)
			return [];
		const role = Object.values(RoleEnum).find((value) => value === permission.roleName);
		if (!role) throw new Error("Select a supported role");
		return [{ id: permission.assignmentId, organizationId: permission.organizationId, role }];
	});
	return {
		assignments,
		removeAssignmentIds: originalRoleAssignments
			.filter(
				(assignment) =>
					!isGlobalRoleAssignment(assignment) &&
					Boolean(assignment.organizationId ?? assignment.organization?.id) &&
					!retainedIds.has(assignment.id),
			)
			.map((assignment) => assignment.id),
	};
};
